import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../components/toast/SuccessToast";
import {AUTHORIZATIONS_PATH} from "../../routes";

export default class AuthorizationFeature {
    constructor(application, navigator, id) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._activeDialog = false
        this._authorizationId = id;
        this._authorization = null
        this._orderPrices = null
        this._advancePaymentProof = null

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _authorization: observable,
        })
    }

    async load() {
        await this.getAuthorization()
        if (this.isAdvancePaymentRequest()){
            await this.orderAdvancePaymentProof()
            await this.getOrderPrices()
        }
        this._finishLoading()
    }

    async getAuthorization() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._authorization = response.getAuthorization()
            },
            handlesError: (response) => {
                errorToast('No se pudo obtener la autorizacion')
            }
        });
        return this._application.apiClient().getAuthorization(this._authorizationId, responseHandler)
    }

    async approveAuthorization() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._navigator(AUTHORIZATIONS_PATH)
                handleSuccessToast("La autorizacion fue actualizada con éxito.")
            },
            handlesError: (response) => this._notifyAuthorizationDecisionFailure(response)
        });
        return this._application.apiClient().approveAuthorization(this.authorization.id, this.authorization.observations, responseHandler)
    }

    async denyAuthorization() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._navigator(AUTHORIZATIONS_PATH)
                handleSuccessToast("La autorización fue denegada con éxito.")
            },
            handlesError: (response) => this._notifyAuthorizationDecisionFailure(response)
        });
        return this._application.apiClient().denyAuthorization(this.authorization.id, this.authorization.observations, responseHandler)
    }

    async requiresChanges() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._navigator(AUTHORIZATIONS_PATH)
                handleSuccessToast("Se han pedido los cambios para la autorizacion con éxito.")
            },
            handlesError: (response) => this._notifyAuthorizationDecisionFailure(response)
        });
        return this._application.apiClient().requiresChanges(this.authorization.id, this.authorization.observations, responseHandler)
    }

    _notifyAuthorizationDecisionFailure(response) {
        const actionRequestHasExpired = response.errors().some((error) => error.code === 'expired_action_request');
        const reasonIsMissing = response.errors().some((error) => error.code === 'reason_is_required_error');
        if (actionRequestHasExpired) {
            errorToast("La fecha de entrega del pedido ya ha expirado, por favor solicite una nueva autorización.");
        } else if (reasonIsMissing) {
            errorToast("Para denegar el pase es obligatorio indicar el motivo.");
        } else {
            errorToast("No se pudo actualizar la autorización.");
        }
    }

    async getOrderPrices() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._orderPrices = response.orderPrices();
            },
            handlesError: async (response) => {}
        });
        return this._application.apiClient().orderPrices(this.order().number, responseHandler)
    }

    async orderAdvancePaymentProof() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._advancePaymentProof = response.advancePaymentProof()
            },
            handlesError: (response) => {}
        });
        return this._application.apiClient().orderAdvancePaymentProof(this.order().number, responseHandler)
    }

    get loaded() {
        return this._loaded;
    }

    _finishLoading() {
        this._loaded = true;
    }

    get authorization(){
        return this._authorization
    }

    set authorization(value) {
        this._authorization = value;
    }

    get orderPrices(){
        return this._orderPrices
    }

    set orderPrices(value) {
        this._orderPrices = value;
    }

    get advancePaymentProof(){
        return this._advancePaymentProof
    }

    set advancePaymentProof(value) {
        this._advancePaymentProof = value;
    }

    setObservation(observation){
        this._authorization.observations = observation
    }

    stateIsClosed(){
        return this._authorization.state === "Denied" || this._authorization.state === "Approved" || this.changesRequired()
    }

    changesRequired() {
        return this._authorization.state === "Changes Required"
    }

    requester() {
        return this._authorization.actionRequest.requester;
    }

    action() {
        return this._authorization.actionRequest.name;
    }

    requestTimestamp() {
        return this._authorization.actionRequest.timestamp;
    }

    order(){
        if (this.action() === 'Pedido Sin Cargo' || this.action() === 'Pedido Con Pago Anticipado'){
            return this._authorization.actionRequest.objectWithActionRequested
        }
    }

    isAdvancePaymentRequest() {
        if (this.action() === 'Pedido Con Pago Anticipado'){
            return true
        }
    }

    isAllowanceConfirmationRequest() {
        return this.action() === 'Bonificación';
    }

    allowance() {
        if (this.isAllowanceConfirmationRequest()) {
            return this._authorization.actionRequest.objectWithActionRequested;
        }
    }

    isJudicialManagementRequest() {
        return this.action() === 'Pase a Gestión Judicial';
    }

    judicialManagementRequest() {
        if (this.isJudicialManagementRequest()) {
            return this._authorization.actionRequest.objectWithActionRequested;
        }
    }

    denyIsDisabled() {
        const reasonIsRequiredButMissing = this.isJudicialManagementRequest() && !this.authorization.observations;
        return this.stateIsClosed() || reasonIsRequiredButMissing;
    }

    taxpayerIdentificationNumber(){
        if (this.action().includes('Alta de Cliente')) {
            return this._authorization.actionRequest.objectWithActionRequested.taxpayerIdentificationNumber;
        }
    }

    deliverySiteOnboardingId(){
        if (this.action().includes('Alta de Punto de Entrega')) {
            return this._authorization.actionRequest.objectWithActionRequested.deliverySiteOnboardingId;
        }
    }

    state() {
        if (this._authorization.state === "Approved") {
            return 'Aprobado';
        } else if (this._authorization.state === "Denied") {
            return 'Denegado';
        } else if (this._authorization.state === "Pending") {
            return 'Pendiente';
        } else if (this.changesRequired()) {
            return 'Cambios Pedidos';
        }
    }
}
